<template>
  <div>
    <div :class="readonly ? 'flex-column' : 'd-flex flex-wrap align-center'">
      <slot name="prepend" />
      <div
        v-for="filter in deserializedFiltersWithValues"
        :class="{ 'mb-4': readonly }"
        :key="filter.key"
        :data-tourid="`explore-filters-${filter.key}Selector`"
      >
        <DeletableFilterWrapper
          :hideDeletableWrapper="hideDeletableWrapper"
          :readonly="readonly"
          @delete="handleDeleteFilter(filter)"
        >
          <AssigneesSelectorFilter
            v-if="filter.key === filtersConstants.filtersKeys.assignees"
            allowClear
            emitAutoApplyMetadata
            includeCurrentAccountAtTop
            :availableAccounts="filter.options"
            :initialAssignees="filter.initialOptions"
            :isLoading="isSearchingAssignees"
            :label="$t('common.assignee_other')"
            :multiple="filter.multiselect"
            :readonly="readonly"
            :selectedAccountIds="filter.value"
            @change="handleAssigneeFilterChange(filter, $event)"
            @clear="handleClearFilter($event, filter)"
            @search="handleSearchAssignees($event)"
          />

          <AssigneesSelectorFilter
            v-else-if="filter.key === filtersConstants.filtersKeys.createdBy"
            allowClear
            emitAutoApplyMetadata
            includeCurrentAccountAtTop
            :availableAccounts="filter.options"
            :id="filter.key"
            :initialAssignees="filter.initialOptions"
            :isLoading="isSearchingUsers"
            :key="filter.key"
            :label="$t('common.created_by')"
            :readonly="readonly"
            :selectedAccountIds="filter.value"
            @change="handleCreatedByFilterChange(filter, $event)"
            @clear="handleClearFilter($event, filter)"
            @search="handleSearchUsers($event)"
          />

          <RoutineDefinitionsFilter
            v-else-if="
              filter.key === filtersConstants.filtersKeys.routineDefinitions
            "
            emitAutoApplyMetadata
            :selectedRoutineIds="filter.value"
            @select="handleChangeFilter($event)"
          />

          <DateRangeFilter
            v-else-if="filter.isDateFilter"
            allowClear
            :allowedDates="filter.allowedDates"
            :dateValues="filter.dates"
            :fixedWidth="fixedWidth"
            :id="filter.key"
            :label="filter.name"
            :options="filter.options"
            :readonly="readonly"
            :value="filter.value"
            @change="handleChangeFilter"
            @delete="handleDeleteFilter({ key: $event })"
          />

          <SelectorFilter
            v-else
            allowClear
            emitAutoApplyMetadata
            :fixedWidth="fixedWidth"
            :readonly="readonly"
            :label="filter.name"
            :id="filter.key"
            :options="filter.options"
            :multiselect="filter.multiselect"
            :value="filter.value"
            @change="handleChangeFilter"
            @delete="handleDeleteFilter({ key: $event })"
          />
        </DeletableFilterWrapper>
      </div>

      <div v-if="!readonly" />

      <template v-if="!readonly">
        <v-tooltip
          :bottom="!!selectedFilters.length"
          :right="!selectedFilters.length"
        >
          <template v-slot:activator="tooltipActivator">
            <div v-on="tooltipActivator.on" class="ms-2 me-6 mb-3">
              <Menu
                right
                max-height="360"
                :options="menuFilters"
                @click="handleAddFilter"
                v-if="menuFilters.length"
              >
                <template v-slot:activator="{ on }">
                  <div v-if="!hideAddButton">
                    <v-btn
                      v-on="on"
                      fab
                      class="add-filter-button"
                      data-tourid="explore-filters-add"
                      elevation="0"
                      color="blue-grey lighten-3"
                      x-small
                    >
                      <v-icon color="blue-grey darken-1">mdi-plus</v-icon>
                    </v-btn>
                  </div>
                </template>
              </Menu>
            </div>
          </template>
          {{ $t('filters.add_filter') }}
        </v-tooltip>

        <div>
          <Button
            v-if="!hideSaveButton && selectedFilters.length"
            class="mx-0 mb-3 ml-2"
            data-tourid="explore-filters-save"
            icon="mdi-filter-variant-plus"
            plain
            size="small"
            :text="$t('filters.save_filter')"
            @click="$emit('save')"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters, mapActions } from 'vuex'
import moment from 'moment'
import { utils } from 'frontend-js-shared'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import routineDefinitionPermissions from '@/mixins/routineDefinitionPermissions.vue'
import trackingMixin from '@/mixins/tracking/trackerMixin.vue'

import Menu from '@/components/UI/Menu.vue'
import Button from '@/components/UI/Button.vue'
import AssigneesSelectorFilter from '@/components/AssigneesSelector/AssigneesSelectorFilter.vue'

import DeletableFilterWrapper from './DeletableFilterWrapper.vue'
import SelectorFilter from './SelectorFilter.vue'
import DateRangeFilter from './DateRangeFilter.vue'
import RoutineDefinitionsFilter from './RoutineDefinitionsFilter.vue'

import filtersConstants from './constants.js'
import { serialize } from './serializer.js'
import { deserialize } from './deserializer.js'
import { buildSiteOptions } from './buildSiteOptions'

const AVAILABLE_FILTERS_KEYS = Object.keys(filtersConstants.filtersKeys)

export default {
  name: 'Filters',

  mixins: [mixin, trackingMixin, routineDefinitionPermissions],

  components: {
    AssigneesSelectorFilter,
    Button,
    DateRangeFilter,
    DeletableFilterWrapper,
    Menu,
    RoutineDefinitionsFilter,
    SelectorFilter,
  },

  props: {
    enableAdditionalFilters: Boolean,
    readonly: Boolean,
    enabledFilters: {
      // Array of keys, key should belong to keys of "availableFilters" computed prop
      type: Array,
      default: () => [], // An empty array will enable all the filters
      validator(value) {
        return (
          value.length === 0 ||
          value.every(val => AVAILABLE_FILTERS_KEYS.includes(val))
        )
      },
    },
    initialFilters: {
      type: Object,
      default: () => {},
    },
    customNames: {
      // Provided names must match filter keys specified in src/components/Filters/constants.js
      type: Object,
      default: () => ({}),
    },
    disableApplyButton: Boolean,
    hideApplyButton: Boolean,
    hideSaveButton: Boolean,
    hideAddButton: Boolean,
    hideDeletableWrapper: Boolean,
    fixedWidth: Number,
  },

  data() {
    return {
      autoAppliedInteractionIds: new Set(),
      selectedFilters: [], // This array will contain objects like those stored in the "availableFilters" computed prop
      isSearchingAssignees: false,
      isSearchingUsers: false,
      accounts: [], // Will contain account objects
      initialAccounts: [], // Will contain account objects
      createdByUserAccounts: [], // Will contain account objects
      filtersConstants,
      defaultFilterNames: {
        sites: this.$t('filters.site_other'),
        types: this.$t('filters.content_type'),
        status: this.$t('filters.status'),
        role: this.$t('filters.role'),
        priority: this.$t('filters.priority'),
        condition: this.$t('filters.condition'),
        approval: this.$t('filters.approvals'),
        validation: this.$t('filters.validation_one'),
        siteGroups: this.$t('filters.site_groups'),
        countryCodes: this.$t('filters.country_other'),
        tags: this.$t('filters.tag_other'),
        tagGroups: this.$t('filters.tag_groups'),
        categories: this.$t('filters.category_other'),
        assignees: this.$t('filters.assignee_other'),
        createdAtOption: this.$t('filters.creation_date'),
        dueDateOption: this.$t('filters.due_date'),
        completedAtOption: this.$t('filters.completed_date'),
        routineDefinitions: this.$t('routines.routine_other'),
        routineType: this.$t('routines.routine_type'),
        activeRoutine: this.$t('routines.active_inactive'),
        situationTypes: this.$t('common.situation_types'),
        routineDefinitionsPresetFilter: this.$t(
          'routines.advanced_selector.filter_routine_by'
        ),
        createdBy: this.$t('common.created_by'),
      },
    }
  },

  mounted() {
    if (!this.organizationCountryCodes.length) {
      this.getOrganizationCountryCodes({
        organizationId: this.organization._id,
      })
    }
    if (!this.organizationCategories.length) {
      this.getOrganizationCategories({ organizationId: this.organization._id })
    }
    if (!this.organizationSituationTypes.length) {
      this.getOrganizationSituationTypes({
        organizationId: this.organization._id,
      })
    }
  },

  watch: {
    initialFilters: {
      immediate: true,
      handler() {
        this.handleInitialFiltersChange()
        this.handleSearchUsers()
      },
    },
  },

  computed: {
    ...mapGetters([
      'account',
      'organization',
      'organizationSites',
      'organizationTags',
      'organizationTagGroups',
      'organizationSiteGroups',
      'organizationCountryCodes',
      'organizationCategories',
      'organizationSituationTypes',
    ]),

    isBoardsEnabled() {
      return this.organization?.featuresEnabled__boards
    },

    filterNames() {
      return {
        ...this.defaultFilterNames,
        ...this.customNames,
      }
    },

    availableFilters() {
      const commonDateOptions = filtersConstants.commonDateOptions.map(
        option => ({
          ...option,
          dates: this.getDateRangeForOption(option.value),
        })
      )

      const defaultFilters = {
        sites: {
          key: 'sites',
          name: this.filterNames.sites,
          multiselect: true,
          options: this.siteOptions,
        },
        types: {
          key: 'types',
          name: this.filterNames.types,
          multiselect: true,
          options: this.typeOptions,
        },
        status: {
          key: 'status',
          name: this.filterNames.status,
          multiselect: true,
          options: this.statusOptions,
        },
        role: {
          key: 'role',
          name: this.filterNames.role,
          multiselect: false,
          options: filtersConstants.roleOptions,
        },
        priority: {
          key: 'priority',
          name: this.filterNames.priority,
          multiselect: true,
          options: filtersConstants.priorityOptions,
        },
        condition: {
          key: 'condition',
          name: this.filterNames.condition,
          multiselect: true,
          options: this.conditionOptions,
        },
        validation: {
          key: 'validation',
          name: this.filterNames.validation,
          multiselect: false,
          options: filtersConstants.validationOptions,
        },
        siteGroups: {
          key: 'siteGroups',
          name: this.filterNames.siteGroups,
          multiselect: true,
          options: this.siteGroupsOptions,
        },
        countryCodes: {
          key: 'countryCodes',
          name: this.filterNames.countryCodes,
          multiselect: true,
          options: this.countryCodeOptions,
        },
        tags: {
          key: 'tags',
          name: this.filterNames.tags,
          multiselect: true,
          options: this.tagsOptions,
        },
        tagGroups: {
          key: 'tagGroups',
          name: this.filterNames.tagGroups,
          multiselect: true,
          options: this.tagGroupsOptions,
        },
        categories: {
          key: 'categories',
          name: this.filterNames.categories,
          multiselect: true,
          options: this.organizationCategoriesOptions,
        },
        assignees: {
          key: 'assignees',
          name: this.filterNames.assignees,
          multiselect: true,
          options: this.accounts,
          initialOptions: this.initialAccounts,
        },
        createdAtOption: {
          key: 'createdAtOption',
          name: this.filterNames.createdAtOption,
          options: commonDateOptions,
          allowedDates: date => date <= moment().format('YYYY-MM-DD'),
          isDateFilter: true,
        },
        dueDateOption: {
          key: 'dueDateOption',
          name: this.filterNames.dueDateOption,
          options: filtersConstants.dueDateOptions.map(option => ({
            ...option,
            dates: this.getDateRangeForOption(option.value),
          })),
          allowedDates: null,
          isDateFilter: true,
        },
        completedAtOption: {
          key: 'completedAtOption',
          name: this.filterNames.completedAtOption,
          options: commonDateOptions,
          allowedDates: null,
          isDateFilter: true,
        },
        createdBy: {
          key: 'createdBy',
          name: this.filterNames.createdBy,
          options: this.createdByUserAccounts,
          initialOptions: [this.initialCreatedByUser || null],
        },
      }

      const additionalFilters = {
        activeRoutine: {
          key: 'activeRoutine',
          name: this.filterNames.activeRoutine,
          multiselect: false,
          options: filtersConstants.activeRoutine,
        },
        routineType: {
          key: 'routineType',
          name: this.filterNames.routineType,
          multiselect: true,
          options: filtersConstants.routineType,
        },
        situationTypes: {
          key: 'situationTypes',
          name: this.filterNames.situationTypes,
          multiselect: true,
          options: this.organizationSituationTypesOptions,
        },
        routineDefinitionsPresetFilter: {
          key: 'routineDefinitionPresetFilter',
          name: this.filterNames.routineDefinitionsPresetFilter,
          multiselect: false,
          options: this.routineDefinitionsPresetFiltersOptions,
        },
      }

      if (this.hasRoutineFeatureEnabled) {
        defaultFilters.routineDefinitions = {
          key: 'routineDefinitions',
          name: this.filterNames.routineDefinitions,
        }
      }

      defaultFilters.approval = {
        key: 'approval',
        name: this.filterNames.approval,
        multiselect: true,
        options: this.approvalOptions,
      }

      return this.enableAdditionalFilters
        ? _.merge(defaultFilters, additionalFilters)
        : defaultFilters
    },

    menuFilters() {
      return _.chain(Object.values(this.availableFilters))
        .map(availableFilter => {
          const filterWasAlreadySelected = Boolean(
            this.selectedFilters.find(
              selectedFilter => selectedFilter.key === availableFilter.key
            )
          )

          const filterWasNotEnabled =
            this.enabledFilters.length &&
            !this.enabledFilters.includes(availableFilter.key)

          if (filterWasAlreadySelected || filterWasNotEnabled) {
            return null
          }

          return {
            id: availableFilter.key,
            key: availableFilter.key,
            title: availableFilter.name,
          }
        })
        .compact()
        .orderBy('title')
        .value()
    },

    deserializedFiltersWithValues() {
      return this.selectedFilters.map(filter => {
        return {
          ...this.availableFilters[filter.key],
          ...filter,
        }
      })
    },

    hasRoutineFeatureEnabled() {
      return Boolean(this.organization?.featuresEnabled__routines)
    },

    isInReviewStatusEnabled() {
      return this.$isFeatureEnabled(this.$featuresKeys.inReviewStatus)
    },

    isPlannerBadgesEnabled() {
      return this.$isFeatureEnabled(this.$featuresKeys.plannerBadges)
    },

    statusOptions() {
      return _.compact([
        {
          text: this.$t('explore.entity_status.open'),
          value: this.$constants.exploreEntityStatus.open,
        },
        this.isInReviewStatusEnabled
          ? {
              value: this.$constants.exploreEntityStatus.inReview,
              text: this.$t('explore.entity_status.in_review'),
            }
          : null,
        {
          value: this.$constants.exploreEntityStatus.inProgress,
          text: this.$t('explore.entity_status.in_progress'),
        },
        {
          value: this.$constants.exploreEntityStatus.completed,
          text: this.$t('explore.entity_status.completed'),
        },
        {
          value: this.$constants.exploreEntityStatus.onHold,
          text: this.$t('explore.entity_status.on_hold'),
        },
        {
          value: this.$constants.exploreEntityStatus.canceled,
          text: this.$t('explore.entity_status.canceled'),
        },
        {
          value: this.$constants.exploreEntityStatus.active,
          text: this.$t('explore.entity_status.active'),
        },

        {
          value: this.$constants.exploreEntityStatus.soon,
          text: this.$t('explore.entity_status.coming_soon'),
        },
        {
          value: this.$constants.exploreEntityStatus.closed,
          text: this.$t('explore.entity_status.closed'),
        },
      ])
    },

    countryCodeOptions() {
      return (
        this.$constants.countries
          .map?.(country => {
            return {
              ...country,
              value: country.code,
              text: this.$t(`countries.${country.code}`),
            }
          })
          .filter?.(country =>
            this.organizationCountryCodes?.some?.(
              countryCode => countryCode === country.code
            )
          ) ?? []
      )
    },

    tagsOptions() {
      return (
        this.organizationTags.map(tag => {
          return {
            value: tag.id,
            text: tag.name,
          }
        }) ?? []
      )
    },

    tagGroupsOptions() {
      return this.organizationTagGroups.map(tagGroup => {
        return {
          value: tagGroup.id,
          text: tagGroup.name,
        }
      })
    },

    siteOptions() {
      return buildSiteOptions({
        accountSiteId: this.account?.site?._id,
        organizationSites: this.organizationSites,
        t: (...args) => this.$t(...args),
      })
    },

    siteGroupsOptions() {
      return this.organizationSiteGroups.map(siteGroup => {
        return {
          text: siteGroup.name,
          value: siteGroup.id,
        }
      })
    },

    typeOptions() {
      return _.compact([
        {
          value: this.$constants.exploreEntityTypes.improvementIdea,
          text:
            this.organization?.customNamings?.['improvementIdea']?.singular ||
            this.$t('common.rev_type.improvement_idea_one'),
        },
        {
          value: this.$constants.exploreEntityTypes.quickAction,
          text:
            this.organization?.customNamings?.['quickAction']?.singular ||
            this.$t('common.rev_type.quick_action_one'),
        },
        {
          value: this.$constants.exploreEntityTypes.problemSolving,
          text:
            this.organization?.customNamings?.['problemSolving']?.singular ||
            this.$t('common.rev_type.problem_solving_one'),
        },
        this.hasRoutineFeatureEnabled && {
          value: this.$constants.exploreEntityTypes.routine,
          text: this.$t('common.routine_one'),
        },
        this.hasRoutineFeatureEnabled && {
          value: this.$constants.exploreEntityTypes.finding,
          text: this.$t('common.finding_one'),
        },

        this.isBoardsEnabled && {
          value: this.$constants.exploreEntityTypes.task,
          text: this.$t('common.task_one'),
          testId: 'explore-filterType-task',
        },
        {
          value: this.$constants.exploreEntityTypes.challenge,
          text: this.$t('common.challenge_one'),
        },
      ])
    },

    approvalOptions() {
      return [
        {
          text: this.$t('common.approved'),
          value: 'isApproved',
        },
        {
          text: this.$t('filters.approval_requested'),
          value: 'isApprovalRequested',
        },
        {
          text: this.$t('filters.waiting_for_my_approval'),
          value: 'waitingForMyApproval',
        },
        {
          text: this.$t('filters.approval_rejected'),
          value: 'isRejected',
        },
      ]
    },
    conditionOptions() {
      const conditionOptions = [
        {
          text: this.$t('common.highlighted'),
          value: 'isHighlighted',
        },
      ]

      conditionOptions.push({
        text: this.$t('filters.is_replica'),
        value: 'isReplica',
      })

      conditionOptions.push({
        text: this.$t('filters.has_replicas'),
        value: 'hasReplicas',
      })

      conditionOptions.push(
        {
          text: this.$t('filters.is_from_finding'),
          value: 'isRevFromFinding',
        },
        {
          text: this.$t('filters.is_not_from_finding'),
          value: 'isNotRevFromFinding',
        }
      )

      if (this.isPlannerBadgesEnabled)
        conditionOptions.push({
          text: this.$t('common.synced_with_planner'),
          value: 'hasPlannerIntegration',
        })

      return conditionOptions
    },

    organizationCategoriesOptions() {
      return this.organizationCategories.map(category => {
        return {
          ...category,
          value: category.name,
          text: this.$t(`common.categories.${category.name}`),
        }
      })
    },

    organizationSituationTypesOptions() {
      return this.organizationSituationTypes.map(situation => {
        return {
          ...situation,
          value: situation.name,
          text: this.$t(
            `quick_action.situation_types.situations.${situation.name}`
          ),
        }
      })
    },

    canCreateRoutines() {
      return this.getRoutineDefinitionPermissions({}).create
    },

    routineDefinitionsPresetFiltersOptions() {
      const options = this.$constants.routines.advancedSelectorOptions.map(
        option => ({
          ...option,
          text: this.$t(option?.name),
        })
      )

      return this.canCreateRoutines
        ? options
        : options.filter(option => option.value !== 'createdByMe')
    },
  },

  methods: {
    ...mapActions([
      'getOrganizationCategories',
      'getOrganizationCountryCodes',
      'getOrganizationSituationTypes',
    ]),

    handleClearFilter(payloadOrFilter, filter = null) {
      this.handleChangeFilter(
        this.buildClearedFilterChange(payloadOrFilter, filter)
      )
    },

    handleAssigneeFilterChange(filter, payload) {
      this.handleChangeFilter(
        this.buildFilterChangeFromPayload(filter, payload)
      )
    },

    handleCreatedByFilterChange(filter, payload) {
      this.handleChangeFilter(
        this.buildFilterChangeFromPayload(filter, payload, value => {
          if (_.isNil(value?.id)) return null

          return value.id
        })
      )
    },

    buildClearedFilterChange(payloadOrFilter, filter = null) {
      const resolvedFilter = filter ?? payloadOrFilter
      const payload = filter ? payloadOrFilter : { value: [] }
      const hasPayloadValue = _.has(payload, 'value')
      let value = null

      if (hasPayloadValue) {
        value = payload.value
      } else if (resolvedFilter.multiselect) {
        value = []
      }

      return {
        ...resolvedFilter,
        ...(hasPayloadValue ? payload : {}),
        value,
      }
    },

    buildFilterChangeFromPayload(
      filter,
      payload,
      formatValue = value => value
    ) {
      const hasPayloadValue = _.has(payload, 'value')
      const nextPayload = hasPayloadValue ? payload : {}
      const rawValue = hasPayloadValue ? payload.value : payload

      return {
        ...filter,
        ...nextPayload,
        value: formatValue(rawValue),
      }
    },

    findSelectedFilter(key) {
      return this.selectedFilters.find(
        selectedFilter => selectedFilter.key === key
      )
    },

    resolveNextFilterValue(filter, selectedFilter) {
      if (filter.key !== filtersConstants.filtersKeys.condition) {
        return filter.value
      }

      return this.normalizeConditionSelection(
        filter.value,
        selectedFilter.value
      )
    },

    updateSelectedFilter(filter) {
      const selectedFilter = this.findSelectedFilter(filter.key)

      if (!selectedFilter) return false

      selectedFilter.value = this.resolveNextFilterValue(filter, selectedFilter)

      if (filter.isDateFilter) {
        selectedFilter.dates = filter?.dates ?? []
      }

      this.selectedFilters = [...this.selectedFilters]
      return true
    },

    emitChangeAndAutoApply() {
      this.emitFiltersChange()
      this.handleApplySelection()
    },

    handleAddFilter(filterAdded) {
      this.selectedFilters.push(filterAdded)
      this.trackFilterActions('filters.addFilter', filterAdded)
    },

    handleDeleteFilter(deletedFilter) {
      this.selectedFilters = this.selectedFilters.filter(
        selectedFilter => selectedFilter.key !== deletedFilter.key
      )
      this.emitChangeAndAutoApply()

      this.trackFilterActions('filters.deleteFilter', {
        name: deletedFilter?.name,
        key: deletedFilter?.key,
      })
    },

    normalizeConditionSelection(nextValue, previousValue) {
      const REV_FROM_FINDING =
        filtersConstants.conditionFilters.isRevFromFinding
      const REV_NOT_FROM_FINDING =
        filtersConstants.conditionFilters.isNotRevFromFinding

      const hasRevFromFinding = nextValue?.includes?.(REV_FROM_FINDING)
      const hasRevNotFromFinding = nextValue?.includes?.(REV_NOT_FROM_FINDING)

      if (!hasRevFromFinding && !hasRevNotFromFinding) return nextValue
      const addedValue = nextValue.find(
        value => !previousValue?.includes?.(value)
      )

      if (addedValue === REV_FROM_FINDING) {
        return nextValue.filter(value => value !== REV_NOT_FROM_FINDING)
      }

      if (addedValue === REV_NOT_FROM_FINDING) {
        return nextValue.filter(value => value !== REV_FROM_FINDING)
      }

      return nextValue
    },

    shouldAutoApplyFilterChange(filter = {}) {
      if (filter.source !== 'user' || !filter.shouldApply) return false

      if (!filter.interactionId) return true
      if (this.autoAppliedInteractionIds.has(filter.interactionId)) return false

      this.autoAppliedInteractionIds.add(filter.interactionId)
      return true
    },

    handleChangeFilter(filter = null) {
      if (!filter) return

      const filterWasUpdated = this.updateSelectedFilter(filter)
      if (!filterWasUpdated) return

      this.emitFiltersChange()

      if (this.shouldAutoApplyFilterChange(filter)) {
        this.handleApplySelection()
      }
    },

    handleApplySelection() {
      this.$emit('apply')
      this.trackFilterActions('filters.applySelection', {
        filters: serialize(this.selectedFilters),
      })
    },

    async handleSearchAssignees(terms = '') {
      try {
        this.isSearchingAssignees = true
        const filters = { isDeleted: false }
        if (_.isEmpty(terms))
          filters.site = utils.getEntityId(this.account?.site)

        const response = await api.account.getAccounts(this.organization._id, {
          filters,
          terms,
        })
        this.accounts = response?.data?.results
      } catch (err) {
        this.showError(err)
      } finally {
        this.isSearchingAssignees = false
      }
    },

    async handleSearchUsers(terms = '') {
      try {
        this.isSearchingUsers = true
        const filters = { isDeleted: false }
        if (_.isEmpty(terms))
          filters.site = utils.getEntityId(this.account?.site)
        const response = await api.account.getAccounts(this.organization._id, {
          filters,
          terms,
        })
        this.createdByUserAccounts = response?.data?.results
      } catch (err) {
        this.showError(err)
      } finally {
        this.isSearchingUsers = false
      }
    },

    async getInitialAccounts(initialAssignees = []) {
      if (!initialAssignees.length) return
      const initialAssigneesArray = _.isArray(initialAssignees)
        ? initialAssignees
        : [initialAssignees]

      const ids = _.compact(initialAssigneesArray)
      if (!ids.length) return

      const response = await api.account.getAccountsByIds(
        this.organization._id,
        {
          filters: { id: ids },
        }
      )
      this.initialAccounts = response.data.results
    },

    async getInitialCreatedByUser(initialCreatedBy) {
      if (!initialCreatedBy) return

      const response = await api.account.getAccountsByIds(
        this.organization._id,
        {
          filters: { id: [initialCreatedBy] },
        }
      )
      this.initialCreatedByUser = response?.data?.results?.[0]
    },

    handleInitialFiltersChange() {
      this.autoAppliedInteractionIds = new Set()
      this.selectedFilters = deserialize(
        _.omit(this.initialFilters, [
          'page',
          'userFilter',
          'userFilterName',
          'view',
          'sort',
        ]),
        this.availableFilters
      )

      this.cleanOrphanApprovalFilterValues()

      if (this.initialFilters?.assignees?.length) {
        this.getInitialAccounts(this.initialFilters.assignees)
      }

      if (this.initialFilters?.createdBy) {
        this.getInitialCreatedByUser(this.initialFilters.createdBy)
      }

      this.emitFiltersChange('initialFiltersLoad')
    },

    cleanOrphanApprovalFilterValues() {
      const approvedFilter = this.selectedFilters.find(
        filter => filter.key === filtersConstants.filtersKeys.approval
      )

      if (!approvedFilter) return

      const validValues = this.approvalOptions.map(option => option.value)
      approvedFilter.value = approvedFilter.value.filter(value =>
        validValues.includes(value)
      )

      if (_.isEmpty(approvedFilter.value)) {
        this.selectedFilters = this.selectedFilters.filter(
          filter => filter.key !== filtersConstants.filtersKeys.approval
        )
      }
    },

    emitFiltersChange(event = 'change') {
      const serializedFilters = serialize(this.selectedFilters)
      this.$emit(event, {
        serializedFilters,
        deserializedFilters: this.selectedFilters,
      })
    },

    getDateRangeForOption(dateRange) {
      const { startDate, endDate } = utils.dates.getPredefinedDateRange(
        dateRange
      )

      return [startDate, endDate]
    },
  },
}
</script>

<style scoped lang="scss">
.add-filter-button:hover {
  background-color: $color-blue-grey-lighten2 !important;
}

.save-filters-button:hover {
  opacity: 0.7;
}
</style>
